import React, { useEffect, useMemo, useState } from "react";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import ExternalLink from "src/app/components/ExternalLink/ExternalLink";
import {
  appendMemberUuidToUrl,
  isInternalServiceUrl,
} from "src/utils/internalServices";
import Skeleton from "src/app/components/Skeleton/Skeleton";
import { openAIChatWithHelpCenterUrl } from "src/redux/ai-chat/aiChatActions";
import "./WelcomeQuickStart.scss";

export interface QuickStartArticle {
  name: string;
  imgSrc: string;
  highlight?: boolean;
  actionButton?: boolean;
  label?: string;
  articles: {
    url: string;
    name: string;
  }[];
}

export interface WelcomePageData {
  quickStartArticleCollection: QuickStartArticle[];
}

const CATEGORY_INDEX_PREFIX_REGEX = /^\s*\d+\s*[.)-]?\s*/;
const DEFAULT_ACTIVE_CATEGORY_INDEX = 1;

function normalizeCategoryName(categoryName: string): string {
  return categoryName.replace(CATEGORY_INDEX_PREFIX_REGEX, "").trim();
}

function getDefaultActiveCategoryIndex(
  categories: QuickStartArticle[],
): number {
  return categories.length > DEFAULT_ACTIVE_CATEGORY_INDEX
    ? DEFAULT_ACTIVE_CATEGORY_INDEX
    : 0;
}

function WelcomeQuickStart() {
  const dispatch = useDispatch();
  const { welcomePageData, identity, isWelcomePageDataLoading } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const quickStartCategories =
    welcomePageData?.quickStartArticleCollection ?? [];

  const [activeCategoryIndex, setActiveCategoryIndex] = useState<number | null>(
    null,
  );
  const [hoveredCategoryIndex, setHoveredCategoryIndex] = useState<
    number | null
  >(null);
  const [selectedArticleUrl, setSelectedArticleUrl] = useState<string | null>(
    null,
  );

  const safeActiveCategoryIndex =
    activeCategoryIndex !== null &&
    activeCategoryIndex >= 0 &&
    activeCategoryIndex < quickStartCategories.length
      ? activeCategoryIndex
      : getDefaultActiveCategoryIndex(quickStartCategories);

  const activeCategory = quickStartCategories[safeActiveCategoryIndex];

  useEffect(() => {
    setSelectedArticleUrl(null);
  }, [safeActiveCategoryIndex]);

  const renderedArticles = useMemo(
    () => activeCategory?.articles ?? [],
    [activeCategory],
  );

  const handleArticleClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    if (!isInternalServiceUrl(url)) return;

    event.preventDefault();
    dispatch(openAIChatWithHelpCenterUrl(url));
  };

  return (
    <section>
      <div className="welcome-quick-start-stage-map">
        <h2 className="welcome-quick-start-stage-map__title">
          <IDHFormattedMessage
            id="ws_homepage_quick_start_title"
            defaultMessage="Quick start"
          />
        </h2>

        <div className="welcome-quick-start-stage-map__body">
          <aside className="welcome-quick-start-stage-map__categories">
            {isWelcomePageDataLoading
              ? Array.from({ length: 8 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div
                    key={index}
                    className="welcome-quick-start-stage-map__category-skeleton-row"
                  >
                    <Skeleton
                      variant="text"
                      width={index % 2 ? 130 : 170}
                      height={13}
                    />
                  </div>
                ))
              : quickStartCategories.map((category, index) => {
                  const categoryLabel = normalizeCategoryName(category.name);
                  const isActive = index === safeActiveCategoryIndex;

                  const isHovered = index === hoveredCategoryIndex;

                  return (
                    <button
                      key={category.name}
                      type="button"
                      className={[
                        "welcome-quick-start-stage-map__category",
                        isActive
                          ? "welcome-quick-start-stage-map__category--active"
                          : "",
                        isHovered && !isActive
                          ? "welcome-quick-start-stage-map__category--hover"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => setActiveCategoryIndex(index)}
                      onMouseEnter={() => setHoveredCategoryIndex(index)}
                      onMouseLeave={() => setHoveredCategoryIndex(null)}
                    >
                      <span className="welcome-quick-start-stage-map__category-label">
                        {categoryLabel}
                      </span>
                      {index === 0 && (
                        <span className="welcome-quick-start-stage-map__new-dot" />
                      )}
                    </button>
                  );
                })}
          </aside>

          <div className="welcome-quick-start-stage-map__content">
            {isWelcomePageDataLoading ? (
              <div
                className="welcome-quick-start-stage-map__skeleton"
                aria-hidden
              >
                <Skeleton
                  variant="rect"
                  width={180}
                  height={28}
                  borderRadius={8}
                />
                <div className="welcome-quick-start-stage-map__skeleton-list">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      variant="rect"
                      width={i % 2 === 0 ? 460 : 620}
                      height={30}
                      borderRadius={100}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <>
                <h3>{normalizeCategoryName(activeCategory?.name ?? "")}</h3>

                <ul className="welcome-quick-start-stage-map__articles">
                  {renderedArticles.map((article) => (
                    <li key={article.url}>
                      <ExternalLink
                        href={appendMemberUuidToUrl(
                          article.url,
                          identity?.id ?? null,
                          window.location.origin,
                        )}
                        className={[
                          "welcome-quick-start-stage-map__article-link",
                          selectedArticleUrl === article.url
                            ? "welcome-quick-start-stage-map__article-link--selected"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={(event) =>
                          handleArticleClick(event, article.url)
                        }
                        onMouseDown={() => setSelectedArticleUrl(article.url)}
                      >
                        <span>{article.name}</span>
                      </ExternalLink>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default WelcomeQuickStart;
